import type { HourlySample } from "@/lib/api/building";
import type { TrendChannel } from "@/lib/chart-data";
import { TREND_CHANNEL_META } from "@/lib/chart-data";

// Tone of a KPI trend delta. Zero is **neutral** — a flat metric must not read as positive (#196).
export type TrendTone = "up" | "down" | "flat";

export function trendTone(trend: number): TrendTone {
  if (trend > 0) return "up";
  if (trend < 0) return "down";
  return "flat";
}

export function valid_trend_channels(channels: string[]): TrendChannel[] {
  return channels.filter((ch): ch is TrendChannel => ch in TREND_CHANNEL_META);
}

// ---------------------------------------------------------------------------
// Multi-building trend metric resolution
// ---------------------------------------------------------------------------

type MultiTrendHourlyKey = keyof Pick<
  HourlySample,
  "energy_kwh" | "temperature" | "occupancy" | "ventilation_rate"
>;

export const MULTI_TREND_METRIC_MAP: Record<
  string,
  { key: MultiTrendHourlyKey; unit: string } | undefined
> = {
  energy_kwh: { key: "energy_kwh", unit: "kWh" },
  indoor_temperature: { key: "temperature", unit: "°C" },
  occupancy: { key: "occupancy", unit: "%" },
  ventilation_rate: { key: "ventilation_rate", unit: "%" },
};

// Chat's LLM tool call omits `metric` when the prompt names no series (e.g. "Piirrä
// monirakennus-trendi kiinteistökannasta") — the wire payload isn't compile-time checked, so
// this can arrive as undefined/"" despite WidgetSpec typing it as required. Default to the same
// estate energy metric the Kiinteistöraportti "energy" template always sets for this widget
// (report-templates.ts `widgets_for_template`).
export const DEFAULT_MULTI_TREND_METRIC = "energy_kwh";

// A missing or empty metric resolves to the estate energy default; a genuinely unknown metric
// string is passed through unchanged so the caller's error can name it instead of the JS word
// "undefined" (#1327).
export function resolve_multi_trend_metric(metric: string | undefined): {
  effective_metric: string;
  meta: { key: MultiTrendHourlyKey; unit: string } | undefined;
} {
  const effective_metric = metric || DEFAULT_MULTI_TREND_METRIC;
  return { effective_metric, meta: MULTI_TREND_METRIC_MAP[effective_metric] };
}
